import { BaseSyntheticEvent, PropsWithChildren } from 'react';
import { useForm, FormProvider, FieldValues, DefaultValues, SubmitHandler, Resolver } from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import type { StandardSchemaV1 } from '@standard-schema/spec';
import { DevTool } from '@hookform/devtools';
import { ZodType } from 'zod';

import { IS_DEV_MODE, USE_DEVTOOLS } from '@/constants/env.constants';
import { SchemaProvider } from '../SchemaProvider';

const handleChildFormSubmit = (callback: (event: BaseSyntheticEvent) => void) => (event: BaseSyntheticEvent) => {
  if (typeof event.stopPropagation === 'function') {
    event.stopPropagation();
  }

  callback(event);
};

type FormPropsType<T extends FieldValues> = {
  defaultValues: DefaultValues<T>;
  schema: ZodType<T>;
  onSubmit: SubmitHandler<T>;
  mode?: 'onSubmit' | 'onBlur' | 'onChange' | 'onTouched' | 'all';
} & PropsWithChildren;

export const ChildForm = <T extends FieldValues>({
  defaultValues,
  schema,
  onSubmit,
  mode = 'onChange',
  children,
}: FormPropsType<T>) => {
  const methods = useForm<T>({
    defaultValues,
    resolver: standardSchemaResolver(schema as StandardSchemaV1<T, T>) as Resolver<T, T>,
    mode,
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleChildFormSubmit(methods.handleSubmit(onSubmit))}>
        <SchemaProvider<T> schema={schema}>{children}</SchemaProvider>
      </form>
      {IS_DEV_MODE && USE_DEVTOOLS && <DevTool control={methods.control} />}
    </FormProvider>
  );
};
